/**
 * PromoCodeDashboard - Promo Code Management
 * Migrated from src/app/components/admin/PromoCodeDashboard.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import DataTable from '../components/common/DataTable';

const { TableSkeleton } = SkeletonLoaderComponents;
import StatusBadge from '../components/common/StatusBadge';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './PromoCodeDashboard.css';

const PromoCodeDashboard = () => {
  const { user: currentUser } = useAdminAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('bulk'); // 'bulk' or 'custom'
  const [generateForm, setGenerateForm] = useState({
    promoType: 'lifetime_vip',
    quantity: 1,
    maxUsage: 1,
    validityDays: 365,
    notes: '',
    extensionDays: 30,
    maxRedemptions: 1000
  });
  const [customCodeForm, setCustomCodeForm] = useState({
    customCode: '',
    promoType: 'trial_extension',
    expiryDays: 365,
    extensionDays: 30,
    maxUses: 1,
    campaignName: '',
    channel: 'email',
    abTestGroup: '',
    notes: ''
  });
  const [validationError, setValidationError] = useState('');
  const [isDuplicateCode, setIsDuplicateCode] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [bulkPattern, setBulkPattern] = useState('');
  const [bulkQuantity, setBulkQuantity] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [redeemersModal, setRedeemersModal] = useState(null);

  const isSuperAdmin = currentUser?.isSuperAdmin || false;

  const fetchPromoCodes = useCallback(async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.PROMO_CODE}/admin/promo-codes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const codes = data.promoCodes || [];
        setPromoCodes(codes);
        calculateStats(codes);
      }
    } catch (err) {
      console.error('[PromoCodeDashboard] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  const calculateStats = (codes) => {
    setStats({
      total: codes.length,
      active: codes.filter(c => c.status === 'active').length,
      used: codes.filter(c => c.usageCount > 0 || c.status === 'redeemed' || c.status === 'used' || c.status === 'exhausted').length,
      revoked: codes.filter(c => c.status === 'revoked').length
    });
  };

  // Validate custom code format
  const validateCustomCode = (code) => {
    if (!code) return '';
    if (code.length < 4) return 'Code must be at least 4 characters';
    if (code.length > 20) return 'Code must be 20 characters or less';
    if (!/^[A-Z0-9-]+$/.test(code)) return 'Only uppercase letters, numbers, and hyphens allowed';
    return '';
  };

  // Check for duplicate code
  const checkDuplicateCode = useCallback((code) => {
    if (!code) return false;
    return promoCodes.some(pc => pc.code.toLowerCase() === code.toLowerCase());
  }, [promoCodes]);

  // Handle custom code input change with validation
  const handleCustomCodeChange = (value) => {
    const upperValue = value.toUpperCase();
    setCustomCodeForm(f => ({ ...f, customCode: upperValue }));
    setValidationError(validateCustomCode(upperValue));
    setIsDuplicateCode(checkDuplicateCode(upperValue));
  };

  // Generate preview codes for bulk pattern
  const generatePatternPreview = (pattern, quantity) => {
    if (!pattern || !pattern.includes('{n}')) return [];
    const preview = [];
    for (let i = 1; i <= Math.min(3, quantity); i++) {
      const code = pattern.replace('{n}', String(i).padStart(3, '0'));
      preview.push(code);
    }
    return preview;
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setActionError(null);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      // Build request body based on promo type
      const requestBody = { ...generateForm };
      
      // Only include extensionDays and maxRedemptions for trial_extension type
      if (generateForm.promoType === 'trial_extension') {
        requestBody.extensionDays = generateForm.extensionDays;
        requestBody.maxRedemptions = generateForm.maxRedemptions;
      } else {
        // Remove these fields for other promo types
        delete requestBody.extensionDays;
        delete requestBody.maxRedemptions;
      }

      const response = await fetch(`${API_ENDPOINTS.ADMIN.PROMO_CODE}/admin/promo-codes/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed: ${response.status}`);
      }

      const data = await response.json();
      setGeneratedCodes(data.promoCodes || []);
      setActionSuccess(`Generated ${data.count || data.promoCodes?.length || 0} promo code(s)`);
      fetchPromoCodes();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateCustomCode = async () => {
    try {
      setGenerating(true);
      setActionError(null);
      
      // Validate before submission
      const error = validateCustomCode(customCodeForm.customCode);
      if (error) {
        setActionError(error);
        setGenerating(false);
        return;
      }
      
      if (isDuplicateCode) {
        setActionError('Code already exists');
        setGenerating(false);
        return;
      }

      if (!customCodeForm.campaignName) {
        setActionError('Campaign name is required for custom codes');
        setGenerating(false);
        return;
      }

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const requestBody = {
        customCode: customCodeForm.customCode,
        promoType: customCodeForm.promoType,
        validityDays: customCodeForm.expiryDays,
        maxUsage: customCodeForm.maxUses,
        campaignName: customCodeForm.campaignName,
        channel: customCodeForm.channel,
        abTestGroup: customCodeForm.abTestGroup || undefined,
        notes: customCodeForm.notes || undefined
      };

      // Add extensionDays for trial_extension type
      if (customCodeForm.promoType === 'trial_extension') {
        requestBody.extensionDays = customCodeForm.extensionDays;
      }

      const response = await fetch(`${API_ENDPOINTS.ADMIN.PROMO_CODE}/admin/promo-codes/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed: ${response.status}`);
      }

      const data = await response.json();
      setGeneratedCodes(data.promoCodes || []);
      setActionSuccess(`Custom code ${customCodeForm.customCode} created successfully`);
      setShowPreview(false);
      fetchPromoCodes();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateBulkCustom = async () => {
    try {
      setGenerating(true);
      setActionError(null);

      if (!bulkPattern.includes('{n}')) {
        setActionError('Pattern must include {n} placeholder');
        setGenerating(false);
        return;
      }

      if (!customCodeForm.campaignName) {
        setActionError('Campaign name is required for custom codes');
        setGenerating(false);
        return;
      }

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const requestBody = {
        pattern: bulkPattern,
        quantity: bulkQuantity,
        promoType: customCodeForm.promoType,
        validityDays: customCodeForm.expiryDays,
        maxUsage: customCodeForm.maxUses,
        campaignName: customCodeForm.campaignName,
        channel: customCodeForm.channel,
        abTestGroup: customCodeForm.abTestGroup || undefined,
        notes: customCodeForm.notes || undefined
      };

      if (customCodeForm.promoType === 'trial_extension') {
        requestBody.extensionDays = customCodeForm.extensionDays;
      }

      const response = await fetch(`${API_ENDPOINTS.ADMIN.PROMO_CODE}/admin/promo-codes/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed: ${response.status}`);
      }

      const data = await response.json();
      setGeneratedCodes(data.promoCodes || []);
      setActionSuccess(`Generated ${data.count || data.promoCodes?.length || 0} custom code(s)`);
      fetchPromoCodes();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (code) => {
    if (!window.confirm(`Revoke promo code ${code}?`)) return;
    try {
      setActionError(null);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.PROMO_CODE}/admin/promo-codes/${encodeURIComponent(code)}/revoke`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Revoked by admin' })
      });

      if (!response.ok) throw new Error(`Failed to revoke: ${response.status}`);
      setActionSuccess(`Promo code ${code} revoked`);
      fetchPromoCodes();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleExport = async () => {
    try {
      setActionError(null);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.PROMO_CODE}/admin/promo-codes/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`Export failed: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `promo-codes-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const columns = [
    {
      key: 'code',
      label: 'Code',
      render: (pc) => <span className="admin-data-table__cell--mono">{pc.code}</span>
    },
    {
      key: 'type',
      label: 'Type',
      render: (pc) => (
        <div>
          <StatusBadge text={pc.promoType} variant="info" />
          {pc.promoType === 'trial_extension' && pc.extensionDays && (
            <span className="promo-code-dashboard__extension-badge">
              +{pc.extensionDays}d
            </span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (pc) => {
        const variant = pc.status === 'active' ? 'success' :
                       pc.status === 'used' || pc.status === 'redeemed' ? 'default' :
                       pc.status === 'revoked' ? 'danger' : 'warning';
        return <StatusBadge text={pc.status} variant={variant} />;
      }
    },
    {
      key: 'usage',
      label: 'Usage',
      render: (pc) => {
        if (pc.promoType === 'trial_extension') {
          return `${pc.currentRedemptions || 0} / ${pc.maxRedemptions || 'N/A'}`;
        }
        return `${pc.usageCount || 0} / ${pc.maxUsage || 1}`;
      }
    },
    {
      key: 'expiry',
      label: 'Expiry',
      render: (pc) => pc.expiryDate ? new Date(pc.expiryDate).toLocaleDateString() : 'Never'
    },
    {
      key: 'redeemedBy',
      label: 'Redeemed By',
      render: (pc) => {
        const redeemers = Array.isArray(pc.redeemedBy) ? pc.redeemedBy : [];
        if (redeemers.length === 0) return <span style={{ color: '#9CA3AF' }}>—</span>;
        // Single redeemer: show inline
        if (redeemers.length === 1) {
          const r = redeemers[0];
          return (
            <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
              <span>{r.userEmail || r.userId || 'Unknown'}</span>
              {r.redeemedAt && (
                <span style={{ color: '#9CA3AF', marginLeft: '4px' }}>
                  ({new Date(r.redeemedAt).toLocaleDateString()})
                </span>
              )}
            </div>
          );
        }
        // Multiple redeemers: show count as clickable link
        return (
          <button
            className="admin-btn admin-btn--link admin-btn--sm"
            style={{ padding: 0, fontSize: '0.8rem', textDecoration: 'underline', background: 'none', border: 'none', color: 'var(--eduq-blue, #0066FF)', cursor: 'pointer' }}
            onClick={() => setRedeemersModal({ code: pc.code, redeemers })}
          >
            {redeemers.length} redeems →
          </button>
        );
      }
    },
    {
      key: 'notes',
      label: 'Notes',
      render: (pc) => {
        if (!pc.notes) return <span style={{ color: '#9CA3AF' }}>—</span>;
        // Truncate long notes with tooltip
        const truncated = pc.notes.length > 30 ? pc.notes.slice(0, 30) + '...' : pc.notes;
        return (
          <span title={pc.notes} style={{ fontSize: '0.8rem', cursor: pc.notes.length > 30 ? 'help' : 'default' }}>
            {truncated}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (pc) => (
        <div>
          {pc.status === 'active' && isSuperAdmin && (
            <button
              className="admin-btn admin-btn--danger admin-btn--sm"
              onClick={() => handleRevoke(pc.code)}
            >
              Revoke
            </button>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return <TableSkeleton rows={8} columns={5} />;
  }

  return (
    <div className="promo-code-dashboard">
      <div className="admin-page-header">
        <h2 className="admin-page-header__title">Promo Code Management</h2>
        <div className="admin-page-header__actions">
          {isSuperAdmin && <span className="promo-code-dashboard__super-badge">🔐 SuperAdmin</span>}
          <div className="admin-btn-group">
            <button className="admin-btn admin-btn--secondary" onClick={handleExport}>
              📥 Export CSV
            </button>
            {isSuperAdmin && (
              <button
                className="admin-btn admin-btn--primary"
                onClick={() => { 
                  setShowGenerateModal(true); 
                  setGeneratedCodes(null); 
                  setActionError(null); 
                  setActiveTab('bulk');
                  setShowPreview(false);
                  setValidationError('');
                  setIsDuplicateCode(false);
                }}
              >
                ➕ Generate Codes
              </button>
            )}
          </div>
        </div>
      </div>

      {actionSuccess && (
        <div className="admin-alert admin-alert--success">
          <span>✅</span> {actionSuccess}
        </div>
      )}
      {actionError && (
        <div className="admin-alert admin-alert--danger">
          <span>❌</span> {actionError}
        </div>
      )}

      {error && (
        <div className="admin-alert admin-alert--warning">
          <span>⚠️</span> {error}
        </div>
      )}

      {stats && (
        <div className="admin-grid admin-grid--4">
          <div className="admin-stat-card">
            <div className="admin-stat-icon purple">🎟️</div>
            <div className="admin-stat-value">{stats.total}</div>
            <div className="admin-stat-label">Total Codes</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon green">✅</div>
            <div className="admin-stat-value">{stats.active}</div>
            <div className="admin-stat-label">Active</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon blue">📊</div>
            <div className="admin-stat-value">{stats.used}</div>
            <div className="admin-stat-label">Used</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon red">🚫</div>
            <div className="admin-stat-value">{stats.revoked}</div>
            <div className="admin-stat-label">Revoked</div>
          </div>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Promo Codes ({promoCodes.length})</h3>
        </div>
        <DataTable
          columns={columns}
          data={promoCodes}
          loading={loading}
          emptyMessage="No promo codes found"
        />
      </div>

      {showGenerateModal && (
        <div className="promo-code-modal-overlay" onClick={() => setShowGenerateModal(false)}>
          <div className="promo-code-modal promo-code-modal--wide" onClick={e => e.stopPropagation()}>
            <div className="promo-code-modal__header">
              <h3 className="promo-code-modal__title">Generate Promo Codes</h3>
              <button onClick={() => setShowGenerateModal(false)} className="promo-code-modal__close">✕</button>
            </div>
            
            {/* Tab Switcher */}
            {!generatedCodes && (
              <div className="admin-tabs" role="tablist" aria-label="Code generation mode">
                <button
                  className={`admin-tab ${activeTab === 'bulk' ? 'admin-tab--active' : ''}`}
                  role="tab"
                  aria-selected={activeTab === 'bulk'}
                  onClick={() => setActiveTab('bulk')}
                >
                  Bulk Generate
                </button>
                <button
                  className={`admin-tab ${activeTab === 'custom' ? 'admin-tab--active' : ''}`}
                  role="tab"
                  aria-selected={activeTab === 'custom'}
                  onClick={() => setActiveTab('custom')}
                >
                  Custom Code
                </button>
              </div>
            )}

            <div>
              {actionError && <div className="admin-alert admin-alert--danger">{actionError}</div>}
              {generatedCodes ? (
                <div className="promo-code-modal__generated">
                  <div className="promo-code-modal__success-header">
                    <div className="promo-code-modal__success-icon">✅</div>
                    <div className="promo-code-modal__success-text">
                      <h4 className="promo-code-modal__success-title">Successfully Generated!</h4>
                      <p className="promo-code-modal__success-subtitle">
                        {generatedCodes.length} promo code{generatedCodes.length !== 1 ? 's' : ''} created
                      </p>
                    </div>
                  </div>

                  <div className="promo-code-modal__codes-container">
                    {generatedCodes.map((c, index) => (
                      <div key={c.code} className="promo-code-card">
                        <div className="promo-code-card__header">
                          <span className="promo-code-card__number">#{index + 1}</span>
                          <button
                            className="promo-code-card__copy-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(c.code);
                              setActionSuccess(`Copied ${c.code} to clipboard`);
                            }}
                            title="Copy to clipboard"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <div className="promo-code-card__code">{c.code}</div>
                        <div className="promo-code-card__details">
                          <div className="promo-code-card__detail">
                            <span className="promo-code-card__detail-label">Type:</span>
                            <span className="promo-code-card__detail-value">{c.promoType}</span>
                          </div>
                          <div className="promo-code-card__detail">
                            <span className="promo-code-card__detail-label">Expires:</span>
                            <span className="promo-code-card__detail-value">
                              {c.expiryDate ? new Date(c.expiryDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              }) : 'Never'}
                            </span>
                          </div>
                          {c.extensionDays && (
                            <div className="promo-code-card__detail">
                              <span className="promo-code-card__detail-label">Extension:</span>
                              <span className="promo-code-card__detail-value">+{c.extensionDays} days</span>
                            </div>
                          )}
                          {c.maxRedemptions && (
                            <div className="promo-code-card__detail">
                              <span className="promo-code-card__detail-label">Max Uses:</span>
                              <span className="promo-code-card__detail-value">{c.maxRedemptions}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="promo-code-modal__actions">
                    <button
                      className="admin-btn admin-btn--secondary"
                      onClick={() => {
                        const allCodes = generatedCodes.map(c => c.code).join('\n');
                        navigator.clipboard.writeText(allCodes);
                        setActionSuccess('All codes copied to clipboard');
                      }}
                    >
                      📋 Copy All Codes
                    </button>
                    <button
                      className="admin-btn admin-btn--primary"
                      onClick={() => {
                        setGeneratedCodes(null);
                        setShowGenerateModal(false);
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Bulk Generate Form */}
                  {activeTab === 'bulk' && (
                    <form onSubmit={e => { e.preventDefault(); handleGenerate(); }} className="promo-code-modal__form">
                      <div className="promo-code-modal__form-group">
                        <label className="promo-code-modal__label">
                          Type:
                          <span className="promo-code-modal__help-icon" title="Select the type of promo code to generate">ℹ️</span>
                        </label>
                        <select 
                          className="promo-code-modal__select"
                          value={generateForm.promoType} 
                          onChange={e => setGenerateForm(f => ({...f, promoType: e.target.value}))}
                        >
                          <option value="lifetime_vip" title="10-year premium access, no card required">Lifetime VIP - 10 years free</option>
                          <option value="annual_trial" title="1-year free trial, card required, auto-charges after 1 year">Annual Trial - 1 year free (card required)</option>
                          <option value="scholarship" title="1-year scholarship access, card required">Scholarship - 1 year free (card required)</option>
                          <option value="trial_extension" title="Extends existing trial by specified days, can be redeemed multiple times">Trial Extension - Extend trial by X days</option>
                        </select>
                      </div>
                      
                      {generateForm.promoType === 'trial_extension' && (
                        <>
                          <div className="promo-code-modal__form-group">
                            <label className="promo-code-modal__label">Extension Days:</label>
                            <input 
                              type="number" 
                              min="1" 
                              max="365"
                              className="promo-code-modal__input"
                              value={generateForm.extensionDays} 
                              onChange={e => setGenerateForm(f => ({...f, extensionDays: parseInt(e.target.value) || 30}))} 
                            />
                            <small className="promo-code-modal__help-text">
                              Number of days to extend the trial (default: 30)
                            </small>
                          </div>
                          <div className="promo-code-modal__form-group">
                            <label className="promo-code-modal__label">Max Redemptions:</label>
                            <input 
                              type="number" 
                              min="1" 
                              className="promo-code-modal__input"
                              value={generateForm.maxRedemptions} 
                              onChange={e => setGenerateForm(f => ({...f, maxRedemptions: parseInt(e.target.value) || 1000}))} 
                            />
                            <small className="promo-code-modal__help-text">
                              Maximum number of times this code can be redeemed (default: 1000)
                            </small>
                          </div>
                        </>
                      )}
                      
                      <div className="promo-code-modal__form-group">
                        <label className="promo-code-modal__label">Quantity (1-100):</label>
                        <input 
                          type="number" 
                          min="1" 
                          max="100" 
                          className="promo-code-modal__input"
                          value={generateForm.quantity} 
                          onChange={e => setGenerateForm(f => ({...f, quantity: parseInt(e.target.value) || 1}))} 
                        />
                      </div>
                      
                      {generateForm.promoType !== 'trial_extension' && (
                        <div className="promo-code-modal__form-group">
                          <label className="promo-code-modal__label">Max Usage:</label>
                          <input 
                            type="number" 
                            min="1" 
                            className="promo-code-modal__input"
                            value={generateForm.maxUsage} 
                            onChange={e => setGenerateForm(f => ({...f, maxUsage: parseInt(e.target.value) || 1}))} 
                          />
                        </div>
                      )}
                      
                      <div className="promo-code-modal__form-group">
                        <label className="promo-code-modal__label">Validity (days):</label>
                        <input 
                          type="number" 
                          min="1" 
                          className="promo-code-modal__input"
                          value={generateForm.validityDays} 
                          onChange={e => setGenerateForm(f => ({...f, validityDays: parseInt(e.target.value) || 365}))} 
                        />
                      </div>
                      <div className="promo-code-modal__form-group">
                        <label className="promo-code-modal__label">Notes:</label>
                        <textarea 
                          className="promo-code-modal__textarea"
                          value={generateForm.notes} 
                          onChange={e => setGenerateForm(f => ({...f, notes: e.target.value}))} 
                          rows={2} 
                        />
                      </div>
                      <div className="admin-btn-group admin-btn-group--lg">
                        <button type="submit" className="admin-btn admin-btn--primary" disabled={generating}>
                          {generating ? 'Generating...' : 'Generate'}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Custom Code Form */}
                  {activeTab === 'custom' && !showPreview && (
                    <form onSubmit={e => { e.preventDefault(); setShowPreview(true); }} className="promo-code-modal__form">
                      {/* Custom Code Name Section */}
                      <div className="promo-code-modal__section">
                        <h4 className="promo-code-modal__section-title">Code Details</h4>
                        
                        <div className="promo-code-modal__form-group">
                          <label className="promo-code-modal__label">
                            Custom Code Name *
                            <span className="promo-code-modal__help-icon" title="4-20 characters, uppercase letters, numbers, and hyphens only">ℹ️</span>
                          </label>
                          <input 
                            type="text" 
                            className={`promo-code-modal__input ${validationError || isDuplicateCode ? 'promo-code-modal__input--error' : ''}`}
                            value={customCodeForm.customCode}
                            onChange={e => handleCustomCodeChange(e.target.value)}
                            placeholder="SUMMER2026"
                            required
                          />
                          {validationError && (
                            <small className="promo-code-modal__error-text">❌ {validationError}</small>
                          )}
                          {!validationError && isDuplicateCode && (
                            <small className="promo-code-modal__error-text">❌ Code already exists</small>
                          )}
                          {!validationError && !isDuplicateCode && customCodeForm.customCode && (
                            <small className="promo-code-modal__success-text">✅ Code available</small>
                          )}
                        </div>

                        <div className="promo-code-modal__form-group">
                          <label className="promo-code-modal__label">
                            Promo Type *
                          </label>
                          <select 
                            className="promo-code-modal__select"
                            value={customCodeForm.promoType}
                            onChange={e => setCustomCodeForm(f => ({...f, promoType: e.target.value}))}
                          >
                            <option value="trial_extension">Trial Extension</option>
                            <option value="annual_trial">Annual Trial</option>
                            <option value="lifetime_vip">Lifetime VIP</option>
                          </select>
                        </div>

                        <div className="promo-code-modal__form-row">
                          <div className="promo-code-modal__form-group">
                            <label className="promo-code-modal__label">Expiry Days (1-365) *</label>
                            <input 
                              type="number" 
                              min="1" 
                              max="365"
                              className="promo-code-modal__input"
                              value={customCodeForm.expiryDays}
                              onChange={e => setCustomCodeForm(f => ({...f, expiryDays: parseInt(e.target.value) || 365}))}
                              required
                            />
                          </div>

                          {customCodeForm.promoType === 'trial_extension' && (
                            <div className="promo-code-modal__form-group">
                              <label className="promo-code-modal__label">Extension Days (1-365) *</label>
                              <input 
                                type="number" 
                                min="1" 
                                max="365"
                                className="promo-code-modal__input"
                                value={customCodeForm.extensionDays}
                                onChange={e => setCustomCodeForm(f => ({...f, extensionDays: parseInt(e.target.value) || 30}))}
                                required
                              />
                            </div>
                          )}

                          <div className="promo-code-modal__form-group">
                            <label className="promo-code-modal__label">Max Uses (1-10000) *</label>
                            <input 
                              type="number" 
                              min="1" 
                              max="10000"
                              className="promo-code-modal__input"
                              value={customCodeForm.maxUses}
                              onChange={e => setCustomCodeForm(f => ({...f, maxUses: parseInt(e.target.value) || 1}))}
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* Marketing Info Section */}
                      <div className="promo-code-modal__section">
                        <h4 className="promo-code-modal__section-title">Marketing Info</h4>
                        
                        <div className="promo-code-modal__form-group">
                          <label className="promo-code-modal__label">
                            Campaign Name *
                            <span className="promo-code-modal__help-icon" title="Name of the marketing campaign">ℹ️</span>
                          </label>
                          <input 
                            type="text" 
                            className="promo-code-modal__input"
                            value={customCodeForm.campaignName}
                            onChange={e => setCustomCodeForm(f => ({...f, campaignName: e.target.value}))}
                            placeholder="Summer 2026 Launch"
                            required
                          />
                        </div>

                        <div className="promo-code-modal__form-group">
                          <label className="promo-code-modal__label">
                            Marketing Channel *
                          </label>
                          <select 
                            className="promo-code-modal__select"
                            value={customCodeForm.channel}
                            onChange={e => setCustomCodeForm(f => ({...f, channel: e.target.value}))}
                          >
                            <option value="email">Email</option>
                            <option value="social_media">Social Media</option>
                            <option value="influencer">Influencer</option>
                            <option value="paid_ads">Paid Ads</option>
                            <option value="organic">Organic</option>
                            <option value="referral">Referral</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div className="promo-code-modal__form-group">
                          <label className="promo-code-modal__label">
                            A/B Test Group
                            <span className="promo-code-modal__help-icon" title="Optional: e.g., A, B, Control">ℹ️</span>
                          </label>
                          <input 
                            type="text" 
                            className="promo-code-modal__input"
                            value={customCodeForm.abTestGroup}
                            onChange={e => setCustomCodeForm(f => ({...f, abTestGroup: e.target.value}))}
                            placeholder="A, B, Control (optional)"
                          />
                        </div>

                        <div className="promo-code-modal__form-group">
                          <label className="promo-code-modal__label">Notes</label>
                          <textarea 
                            className="promo-code-modal__textarea"
                            value={customCodeForm.notes}
                            onChange={e => setCustomCodeForm(f => ({...f, notes: e.target.value}))}
                            rows={2}
                            placeholder="Optional notes about this code"
                          />
                        </div>
                      </div>

                      {/* Bulk Pattern Section (optional) */}
                      <div className="promo-code-modal__section">
                        <h4 className="promo-code-modal__section-title">
                          Bulk Pattern Generation (Optional)
                          <span className="promo-code-modal__help-icon" title="Generate multiple codes with a pattern">ℹ️</span>
                        </h4>
                        
                        <div className="promo-code-modal__form-group">
                          <label className="promo-code-modal__label">
                            Pattern (use {'{n}'} for counter)
                          </label>
                          <input 
                            type="text" 
                            className="promo-code-modal__input"
                            value={bulkPattern}
                            onChange={e => setBulkPattern(e.target.value.toUpperCase())}
                            placeholder="SUMMER2026-{n}"
                          />
                          <small className="promo-code-modal__help-text">
                            Leave empty to create single code. Use {'{n}'} for sequential numbers (001, 002, etc.)
                          </small>
                        </div>

                        {bulkPattern && bulkPattern.includes('{n}') && (
                          <>
                            <div className="promo-code-modal__form-group">
                              <label className="promo-code-modal__label">Quantity (1-100)</label>
                              <input 
                                type="number" 
                                min="1" 
                                max="100"
                                className="promo-code-modal__input"
                                value={bulkQuantity}
                                onChange={e => setBulkQuantity(parseInt(e.target.value) || 10)}
                              />
                            </div>

                            <div className="promo-code-modal__preview">
                              <div className="promo-code-modal__preview-label">Preview (first 3):</div>
                              <div className="promo-code-modal__preview-codes">
                                {generatePatternPreview(bulkPattern, bulkQuantity).map((code, i) => (
                                  <span key={i} className="promo-code-modal__preview-code">{code}</span>
                                ))}
                                {bulkQuantity > 3 && <span className="promo-code-modal__preview-more">... +{bulkQuantity - 3} more</span>}
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="admin-btn-group admin-btn-group--lg">
                        <button 
                          type="submit" 
                          className="admin-btn admin-btn--primary" 
                          disabled={generating || validationError || isDuplicateCode}
                        >
                          {generating ? 'Generating...' : bulkPattern && bulkPattern.includes('{n}') ? 'Generate Bulk Codes' : 'Preview Code'}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Preview Modal for Custom Code */}
                  {activeTab === 'custom' && showPreview && (
                    <div className="promo-code-modal__preview-container">
                      <h4 className="promo-code-modal__preview-title">Preview Custom Code</h4>
                      
                      <div className="promo-code-modal__preview-details">
                        <div className="promo-code-modal__preview-item">
                          <span className="promo-code-modal__preview-label">Code:</span>
                          <span className="promo-code-modal__preview-value promo-code-modal__preview-value--code">{customCodeForm.customCode}</span>
                        </div>
                        <div className="promo-code-modal__preview-item">
                          <span className="promo-code-modal__preview-label">Type:</span>
                          <span className="promo-code-modal__preview-value">{customCodeForm.promoType}</span>
                        </div>
                        <div className="promo-code-modal__preview-item">
                          <span className="promo-code-modal__preview-label">Campaign:</span>
                          <span className="promo-code-modal__preview-value">{customCodeForm.campaignName}</span>
                        </div>
                        <div className="promo-code-modal__preview-item">
                          <span className="promo-code-modal__preview-label">Channel:</span>
                          <span className="promo-code-modal__preview-value">{customCodeForm.channel}</span>
                        </div>
                        {customCodeForm.abTestGroup && (
                          <div className="promo-code-modal__preview-item">
                            <span className="promo-code-modal__preview-label">A/B Group:</span>
                            <span className="promo-code-modal__preview-value">{customCodeForm.abTestGroup}</span>
                          </div>
                        )}
                        <div className="promo-code-modal__preview-item">
                          <span className="promo-code-modal__preview-label">Expiry:</span>
                          <span className="promo-code-modal__preview-value">{customCodeForm.expiryDays} days</span>
                        </div>
                        {customCodeForm.promoType === 'trial_extension' && (
                          <div className="promo-code-modal__preview-item">
                            <span className="promo-code-modal__preview-label">Extension:</span>
                            <span className="promo-code-modal__preview-value">{customCodeForm.extensionDays} days</span>
                          </div>
                        )}
                        <div className="promo-code-modal__preview-item">
                          <span className="promo-code-modal__preview-label">Max Uses:</span>
                          <span className="promo-code-modal__preview-value">{customCodeForm.maxUses}</span>
                        </div>
                      </div>

                      <div className="admin-btn-group admin-btn-group--lg">
                        <button 
                          type="button"
                          className="admin-btn admin-btn--secondary" 
                          onClick={() => setShowPreview(false)}
                        >
                          ← Back
                        </button>
                        <button 
                          type="button"
                          className="admin-btn admin-btn--primary" 
                          onClick={bulkPattern && bulkPattern.includes('{n}') ? handleGenerateBulkCustom : handleGenerateCustomCode}
                          disabled={generating}
                        >
                          {generating ? 'Creating...' : 'Create Code'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Redeemers Modal — shows list of users who redeemed a multi-use code */}
      {redeemersModal && (
        <div className="promo-code-modal-overlay" onClick={() => setRedeemersModal(null)}>
          <div className="promo-code-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="promo-code-modal__header">
              <h3 className="promo-code-modal__title">Redemptions — {redeemersModal.code}</h3>
              <button onClick={() => setRedeemersModal(null)} className="promo-code-modal__close">✕</button>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>#</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>User</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>Plan</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {redeemersModal.redeemers.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '8px 12px', color: '#9CA3AF' }}>{i + 1}</td>
                      <td style={{ padding: '8px 12px' }}>{r.userEmail || r.userId || 'Unknown'}</td>
                      <td style={{ padding: '8px 12px', color: '#6B7280' }}>{r.planSelected || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#6B7280' }}>
                        {r.redeemedAt ? new Date(r.redeemedAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', textAlign: 'right' }}>
              <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => setRedeemersModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodeDashboard;
